
import { Rect, G, SVG, Use, Text } from '@svgdotjs/svg.js';
import { DefaultInsertDims, ICONS_URL, INNER_RECT_OFFSET_PX, Measurement } from '../lib/constants';
import { InsertConfigData, InsertData, InsertType, InsertUpdateData, ModeType } from '../lib/types';
import { deleteInsert$ } from '../lib/state';
import Logger from '../../js/lib/logger';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { getFancyDimString } from '../lib/util.functions';

export default class Insert {
  #NAME = 'Insert';
  element: SVGGElement;
  #id: string;
  get id(): string {
    return this.#id;
  }
  #type: InsertType;
  get type(): InsertType {
    return this.#type;
  }
  #group: G;
  #delete: G;
  #rect: Rect;
  get rect(): Rect {
    return this.#rect;
  }
  #innerRect: Rect;
  #infoGroup: G;
  #icon: Use | undefined;
  #text1: Text;
  #text2: Text;
  #modified = false;
  #width: number;
  #height: number;
  #unitWidth: number;
  #unitHeight: number;
  #x: number;
  #y: number;
  #title: string;
  #mode: ModeType = 'nuetral';
  get mode(): ModeType {
    return this.#mode;
  }
  set mode(val: ModeType) {
    this.#mode = val;
    this.element.dataset.mode = this.#mode;
  }
  
  get data(): InsertData {
    const obj = {
      id: this.#id,
      type: this.#type,
      width: this.#width,
      height: this.#height,
      unitWidth: this.#unitWidth,
      unitHeight: this.#unitHeight,
      x: this.#x,
      y: this.#y,
      modified: this.#modified
    }
    /* Logger.info(`${this.#NAME} > get data >`, obj); */
    return obj;
  }
  set data(val: InsertUpdateData) {
    /* Logger.info(`${this.#NAME} > set data >`, val); */
    if(val.width !== this.#width || val.height !== this.#height) {
      this.#modified = true;
    }
    if (val.width !== undefined) {
      this.#width = val.width >= Measurement.MIN_INSERT_DIM_PX ? val.width : Measurement.MIN_INSERT_DIM_PX;
      this.#unitWidth = this.#width / Measurement.UNIT_PX;
    }
    if (val.height !== undefined) {
      this.#height = val.height >= Measurement.MIN_INSERT_DIM_PX ? val.height : Measurement.MIN_INSERT_DIM_PX;
      this.#unitHeight = this.#height / Measurement.UNIT_PX;
    }
    if (val.x !== undefined) this.#x = val.x;
    if (val.y !== undefined) this.#y = val.y;
    this.#update();
  }

  constructor({type, id, x = 0, y = 0, unitWidth = 0, unitHeight = 0, title = undefined}: InsertConfigData){
    Logger.info(`${this.#NAME} > constructor > ${type} > ${id}`);
    this.#id = id;
    this.#type = type;
    this.#x = x || 0;
    this.#y = y || 0;
    const { width, height } = Insert.getWidthHeight(type, unitWidth, unitHeight);
    this.#width = width;
    this.#height = height;
    //
    switch (type) {
      case 'belt':
        this.#title = 'Tie/Belt';
        break;

      case 'cushion':
        this.#title = 'Ring Cushion';
        break;

      case 'custom':
        this.#title = title || 'Custom';
        break;

      case 'earrings':
        this.#title = 'Earrings';
        break;

      case 'necklace':
        this.#title = 'Necklaces';
        break;

      case 'square':
        this.#title = 'Plain Square';
        break;

      case 'watch':
        this.#title = 'Watch/Bracelets';
        break;

      case 'pillow':
        this.#title = 'Watch Pillow';
        break;

      case 'sunglasses':
        this.#title = 'Sunglasses';
        break;

      default:
        this.#title = 'Insert';
        break;
    };
    //
    this.#unitWidth = this.#width / Measurement.UNIT_PX;
    this.#unitHeight = this.#height / Measurement.UNIT_PX;
    //
    this.#group = SVG().group().attr({ id: this.id, class: 'insert' });
    this.element = this.#group.node;
    this.element.dataset.mode = this.#mode;
    this.#rect = this.#group.rect(this.#width, this.#height).attr({class: 'outer-rect'});
    //
    this.#innerRect = this.#group.rect(this.#width - Measurement.SEP_WIDTH_PX, this.#height - Measurement.SEP_WIDTH_PX)
                                .attr({ class: 'inner-rect', x: INNER_RECT_OFFSET_PX, y: INNER_RECT_OFFSET_PX });
    //
    this.#delete = SVG().group().attr({ class: 'delete-btn' }).addTo(this.#group);
    this.#delete.rect(13,13);
    this.#delete.use('close', ICONS_URL).attr({ width: 13, height: 13 });
    this.#delete.on('pointerdown', () => this.remove());
    //
    this.#infoGroup = SVG().group().attr({ class: 'info-group' }).addTo(this.#group);
    if (type !== 'custom' && type !== 'cushion') this.#icon = this.#infoGroup.use(type === 'pillow' ? 'watch' : type, ICONS_URL).attr({ width: 20, height: 20 });
    this.#text1 = this.#infoGroup.text(this.#title).attr({ 'text-anchor': 'middle', class: 'text-1' });
    this.#text2 = this.#infoGroup.text(getFancyDimString(this.#unitWidth, this.#unitHeight)).attr({ 'text-anchor': 'middle', class: 'text-2' });
    //
    this.#update();
  }

  static getWidthHeight(type: InsertType, unitWidth?: number, unitHeight?: number): { width: number, height: number} {
    const dims = { width: 0, height: 0 };
    switch (type) {
      case 'belt':
        dims.width = unitWidth ? (Measurement.UNIT_PX * unitWidth) : (Measurement.UNIT_PX * DefaultInsertDims.BELT.width);
        dims.height = unitHeight ? (Measurement.UNIT_PX * unitHeight) : (Measurement.UNIT_PX * DefaultInsertDims.BELT.height);
        break;

      case 'cushion':
        dims.width = Measurement.UNIT_PX * (unitWidth || DefaultInsertDims.CUSHION.width);
        dims.height = Measurement.UNIT_PX * (unitHeight || DefaultInsertDims.CUSHION.height);
        break;

      case 'custom':
        dims.width = Measurement.UNIT_PX * (unitWidth || DefaultInsertDims.CUSTOM.width);
        dims.height = Measurement.UNIT_PX * (unitHeight || DefaultInsertDims.CUSTOM.height);
        break;

      case 'earrings':
        dims.width = Measurement.UNIT_PX * (unitWidth || DefaultInsertDims.EARRINGS.width);
        dims.height = Measurement.UNIT_PX * (unitHeight || DefaultInsertDims.EARRINGS.height);
        break;

      case 'necklace':
        dims.width = Measurement.UNIT_PX * (unitWidth || DefaultInsertDims.NECKLACE.width);
        dims.height = Measurement.UNIT_PX * (unitHeight || DefaultInsertDims.NECKLACE.height);
        break;

      case 'square':
        dims.width = Measurement.UNIT_PX * (unitWidth || DefaultInsertDims.SQUARE.width);
        dims.height = Measurement.UNIT_PX * (unitHeight || DefaultInsertDims.SQUARE.height);
        break;

      case 'watch':
        dims.width = unitWidth ? (Measurement.UNIT_PX * unitWidth) : (Measurement.UNIT_PX * DefaultInsertDims.WATCH.width);
        dims.height = unitHeight ? (Measurement.UNIT_PX * unitHeight) : (Measurement.UNIT_PX * DefaultInsertDims.WATCH.width);
        break;

      case 'pillow':
        dims.width = unitWidth ? (Measurement.UNIT_PX * unitWidth) : (Measurement.UNIT_PX * DefaultInsertDims.PILLOW.width);
        dims.height = unitHeight ? (Measurement.UNIT_PX * unitHeight) : (Measurement.UNIT_PX * DefaultInsertDims.PILLOW.width);
        break;

      case 'sunglasses':
        dims.width = unitWidth ? (Measurement.UNIT_PX * unitWidth) : (Measurement.UNIT_PX * DefaultInsertDims.SUNGLASSES.width);
        dims.height = unitHeight ? (Measurement.UNIT_PX * unitHeight) : (Measurement.UNIT_PX * DefaultInsertDims.SUNGLASSES.height);
        break;

      default:
        break;
    };
    return dims;
  }

  #update() {
    let iconHeight = this.#icon?.bbox().height || 0;
    if (this.#icon) iconHeight = iconHeight > 16 ? 16 : (iconHeight < 10 ? 10 : iconHeight);
    gsap.set(this.#infoGroup.node, { scale: 1, transformOrigin: 'center' });
    const t1BBox = this.#text1.bbox();
    const t2BBox = this.#text2.bbox();
    this.#rect.attr({ width: this.#width, height: this.#height});
    this.#innerRect.attr({ width: this.#width - Measurement.SEP_WIDTH_PX, height: this.#height - Measurement.SEP_WIDTH_PX});
    gsap.set(this.#group.node, { x: this.#x, y: this.#y });
    this.#delete.attr({ transform: `matrix(1,0,0,1,${this.#width - 18},5)`});
    this.#icon?.attr({ x: (this.#width / 2) - 10, y: 0 });
    this.#text1.attr({ dx: this.#width / 2, y: iconHeight + t1BBox.height + (iconHeight ? 4 : 0) });
    /* display interior dims */
    this.#text2.clear().tspan(getFancyDimString(this.#unitWidth - Measurement.SEP_WIDTH, this.#unitHeight - Measurement.SEP_WIDTH));
    /* display actual dims */
    // this.#text2.clear().tspan(getFancyDimString(this.#unitWidth, this.#unitHeight));
    this.#text2.attr({ dx: this.#width / 2, y: iconHeight + t1BBox.height + t2BBox.height + (iconHeight ? 8 : 4) });
    const h = iconHeight + t1BBox.height + t2BBox.height + (iconHeight ? 8 : 4);
    const shortest = this.#width <= this.#height ? this.#width : this.#height;
    const scale = t1BBox.width > (shortest - 8) ? (shortest - 8) / t1BBox.width : 1;
    const y = ((this.#height / 2) - (h / 2)).toFixed(2);
    gsap.set(this.#infoGroup.node, { y, scale, transformOrigin: 'center' });
  }

  remove() {
    Logger.info(`${this.#NAME} > #remove > ${this.#type} > ${this.id}`);
    Draggable.get(this.element)?.kill();
    this.element.remove();
    this.#delete.off('click');
    deleteInsert$.next(this.id);
  }
}